import { useQueries } from '@tanstack/react-query'
import { useMemo, useSyncExternalStore } from 'react'
import { listAutocomplete, STALE_TIME } from 'api/autocomplete'
import { qkBaseAutocomplete } from 'constants/QueryKeys/autocomplete'
import { AutocompleteInfo, AutocompleteListParams, AutocompleteResponse } from 'types/autocomplete'
import type { IFilterList } from './DataTable'

/**
 * Autocomplete endpoints only return the first N matches, so dropdown filters backed by them
 * must search server-side (`q`) instead of filtering a preloaded list on the client.
 */
export type AutocompleteFilterSource = {
  queryKey: (searchValue: string) => unknown[]
  queryFn: (searchValue: string) => Promise<AutocompleteResponse>
  valueField?: 'id' | 'name'
  enabled?: boolean
  filterItem?: (item: AutocompleteInfo) => boolean
}

type AutocompleteFilterSourceOptions = {
  skipCurrencyFilter?: boolean
  valueField?: 'id' | 'name'
  enabled?: boolean
  filterItem?: (item: AutocompleteInfo) => boolean
  /** Extra query key parts, e.g. the currency filter when the list depends on it. */
  keyParts?: unknown[]
}

export const autocompleteFilterSource = (
  endpoint: string,
  params: Omit<AutocompleteListParams, 'q'> = {},
  { skipCurrencyFilter, valueField, enabled, filterItem, keyParts = [] }: AutocompleteFilterSourceOptions = {}
): AutocompleteFilterSource => ({
  queryKey: (q) => [...qkBaseAutocomplete, 'filter', endpoint, params, skipCurrencyFilter, ...keyParts, q],
  queryFn: (q) => listAutocomplete(endpoint, { ...params, q }, { skipCurrencyFilter }),
  valueField,
  enabled,
  filterItem,
})

/***************************
 * Known items store
 ***************************/
// Every item returned for a source (initial list + searches) is kept so selected values
// outside the current results can still resolve their label (chips) and company type.
const knownItems = new Map<string, Map<string, AutocompleteInfo>>()
const listeners = new Set<() => void>()
let storeVersion = 0

const sourceKey = (source: AutocompleteFilterSource) => JSON.stringify(source.queryKey(''))

const registerItems = (source: AutocompleteFilterSource, items: AutocompleteInfo[]) => {
  const key = sourceKey(source)
  const sourceItems = knownItems.get(key) ?? new Map<string, AutocompleteInfo>()
  knownItems.set(key, sourceItems)
  let changed = false
  items.forEach((item) => {
    if (sourceItems.get(item.id) !== item) {
      sourceItems.set(item.id, item)
      changed = true
    }
  })
  if (changed) {
    storeVersion++
    listeners.forEach((listener) => listener())
  }
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getStoreVersion = () => storeVersion

export const fetchAutocompleteFilterSource = async (source: AutocompleteFilterSource, q: string) => {
  const response = await source.queryFn(q)
  registerItems(source, response?.data ?? [])
  return response
}

export const toAutocompleteFilterOptions = (items: AutocompleteInfo[], source: AutocompleteFilterSource) =>
  items
    .filter((item) => !source.filterItem || source.filterItem(item))
    .map((item) => ({ label: item.name, value: source.valueField === 'name' ? item.name : item.id }))

const optionKey = (option: unknown) =>
  option && typeof option === 'object' ? (option as { value?: unknown }).value : option

/** True when both filter values select the same option(s), even if they are different object instances. */
export const isSameAutocompleteFilterValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((option, index) => optionKey(option) === optionKey(b[index]))
  }
  if (Array.isArray(a) || Array.isArray(b) || !a || !b) return false
  return optionKey(a) === optionKey(b)
}

/** Every item known for the source, e.g. to resolve whether a selected company is a client or a broker. */
export const getAutocompleteFilterResponse = (source: AutocompleteFilterSource): AutocompleteResponse => ({
  data: Array.from(knownItems.get(sourceKey(source))?.values() ?? []),
})

/**
 * Loads the initial list of each autocomplete filter and fills its `dropdownProps.options` with every
 * known item, so the existing chip/selected-value lookups keep working for values outside the first page.
 */
export function useAutocompleteFilters<T extends IFilterList>(filters: T[]): T[]
export function useAutocompleteFilters<T extends IFilterList>(filters: T[] | undefined): T[] | undefined
export function useAutocompleteFilters<T extends IFilterList>(filters: T[] | undefined): T[] | undefined {
  const sources = (filters ?? []).flatMap((filter) => (filter.autocomplete ? [filter.autocomplete] : []))
  useQueries({
    queries: sources.map((source) => ({
      queryKey: source.queryKey(''),
      queryFn: () => fetchAutocompleteFilterSource(source, ''),
      enabled: source.enabled ?? true,
      staleTime: STALE_TIME,
    })),
  })
  const version = useSyncExternalStore(subscribe, getStoreVersion)

  return useMemo(() => {
    if (!filters?.some((filter) => filter.autocomplete)) return filters
    return filters.map((filter) => {
      const source = filter.autocomplete
      if (!source) return filter
      const items = knownItems.get(sourceKey(source))
      return {
        ...filter,
        dropdownProps: {
          ...filter.dropdownProps,
          options: items ? toAutocompleteFilterOptions(Array.from(items.values()), source) : undefined,
        },
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, version])
}
